"use client";

import { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";

import {
  BrandColors,
  DEFAULT_BRAND_COLORS,
  ThemeColors,
  colorToCssVar,
  deriveThemeFromBrand,
} from "@/lib/themes";

import { Tenant } from "@/types/tenant";

import { fetchTenant } from "@/api/tenant";

interface BrandThemeProviderProps {
  /** Explicit brand colors. Takes precedence over `tenant`. */
  brand?: Partial<BrandColors>;
  /** Reads brand colors from this tenant. */
  tenant?: Pick<Tenant, "brandPrimaryColor" | "brandBackgroundColor">;
  /**
   * When true, fetches the public tenant via subdomain from the client. Prefer
   * passing `tenant` from a server component so SSR HTML already carries the
   * brand (via `global`). This is only for cases where the tenant isn't known
   * server-side yet.
   */
  fetchFromSubdomain?: boolean;
  /**
   * Apply the theme to <html> instead of a scoped wrapper. Required so
   * portaled components (dialogs, popovers, toasts) — which render at the
   * document body — inherit the brand colors. Use on patient-facing pages.
   * The scoped default is for settings previews embedded in the console.
   */
  global?: boolean;
  children: ReactNode;
}

function resolveBrand(
  explicit?: Partial<BrandColors>,
  tenant?: BrandThemeProviderProps["tenant"],
): BrandColors {
  return {
    primary:
      explicit?.primary ??
      tenant?.brandPrimaryColor ??
      DEFAULT_BRAND_COLORS.primary,
    background:
      explicit?.background ??
      tenant?.brandBackgroundColor ??
      DEFAULT_BRAND_COLORS.background,
  };
}

function buildStyles(colors: ThemeColors): CSSProperties {
  const styles: Record<string, string> = {};
  (Object.keys(colors) as (keyof ThemeColors)[]).forEach((key) => {
    const cssVar = colorToCssVar[key];
    if (cssVar) styles[cssVar] = colors[key];
  });
  return styles as CSSProperties;
}

/**
 * Serialize the brand colors into a CSS rule against `:root` so the first
 * server-rendered paint is already themed. This is the same set of variables
 * the client-side effect writes to `document.documentElement`, but inlined
 * into the HTML so it applies before React hydrates.
 */
function buildRootCss(colors: ThemeColors, baseMode: "light" | "dark"): string {
  const declarations = (Object.keys(colors) as (keyof ThemeColors)[])
    .map((key) => {
      const cssVar = colorToCssVar[key];
      return cssVar ? `${cssVar}:${colors[key]};` : "";
    })
    .join("");
  return `:root{${declarations}color-scheme:${baseMode};}`;
}

/**
 * Applies tenant brand colors via CSS variables. Pass `global` to apply them
 * to <html> (required on patient-facing pages so portaled dialogs/popovers
 * inherit the brand). The scoped default is for settings previews embedded
 * in the console.
 */
export function BrandThemeProvider({
  brand,
  tenant,
  fetchFromSubdomain,
  global,
  children,
}: BrandThemeProviderProps) {
  const [fetchedTenant, setFetchedTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    if (!fetchFromSubdomain || tenant) return;
    let cancelled = false;
    fetchTenant().then((t) => {
      if (!cancelled) setFetchedTenant(t);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchFromSubdomain, tenant]);

  const activeTenant = tenant ?? fetchedTenant ?? undefined;
  const resolved = resolveBrand(brand, activeTenant);
  // Depend on the string fields rather than `resolved` itself — `resolveBrand`
  // returns a fresh object each render, so an object-identity dep would defeat
  // the memo and make the useEffect below re-run (and re-write CSS vars on
  // <html>) on every parent render.
  const { colors, baseMode } = useMemo(
    () => deriveThemeFromBrand(resolved),
    [resolved.primary, resolved.background],
  );

  useEffect(() => {
    if (!global) return;
    const root = document.documentElement;
    const vars = Object.entries(colors)
      .map(([key, value]) => {
        const cssVar = colorToCssVar[key as keyof ThemeColors];
        return cssVar ? ([cssVar, value] as const) : null;
      })
      .filter((entry): entry is readonly [string, string] => entry !== null);

    const previousVars = vars.map(
      ([cssVar]) => [cssVar, root.style.getPropertyValue(cssVar)] as const,
    );
    const previousColorScheme = root.style.colorScheme;
    const hadLight = root.classList.contains("light");
    const hadDark = root.classList.contains("dark");

    vars.forEach(([cssVar, value]) => root.style.setProperty(cssVar, value));
    root.classList.remove("light", "dark");
    root.classList.add(baseMode);
    root.style.colorScheme = baseMode;

    return () => {
      previousVars.forEach(([cssVar, value]) => {
        if (value) root.style.setProperty(cssVar, value);
        else root.style.removeProperty(cssVar);
      });
      root.classList.remove("light", "dark");
      if (hadLight) root.classList.add("light");
      if (hadDark) root.classList.add("dark");
      root.style.colorScheme = previousColorScheme;
    };
  }, [global, colors, baseMode]);

  if (global) {
    return (
      <>
        <style
          // SSR inlines this into the HTML so the first paint is already in
          // the tenant's brand (no flash of default sage/cream before the
          // client effect runs). The effect above keeps it in sync for later
          // updates (e.g., the settings preview).
          dangerouslySetInnerHTML={{ __html: buildRootCss(colors, baseMode) }}
        />
        {children}
      </>
    );
  }

  return (
    <div
      className={baseMode === "dark" ? "dark" : "light"}
      style={{ ...buildStyles(colors), minHeight: "inherit" }}
    >
      {children}
    </div>
  );
}
